import React, { useState } from "react";
import { dayDetails } from "../../../lib/quoteFields.js";
import { C, font, mono, btnP, btnS } from "../../../lib/styles.js";
import { fmtMoney } from "../../../lib/utils.js";

/* The AI box (docs/quotes/ai-box-plan.md). A textarea, so a pasted multi-line instruction
   keeps its lines. Nothing here decides anything: every word comes from the preview
   (lib/quoteBoxPreview.js), so it is tested without a browser. */

const OUTCOME = {
  done: { label: "Done", color: C.green },
  partly: { label: "Partly done", color: C.amber },
  not_done: { label: "Not done", color: C.danger },
};

function RequestCard({ r }) {
  const o = OUTCOME[r.outcome];
  return (
    <div data-testid="box-request" data-outcome={r.outcome} style={S.request}>
      <div style={S.requestHead}>
        <div style={S.words}>{r.words}</div>
        <span style={{ ...S.badge, color: o.color, borderColor: `${o.color}55` }}>{o.label}</span>
      </div>
      {r.changed.length > 0 && <div style={S.note}>Changes: {r.changed.join("; ")}</div>}
      {r.noOutcomeGiven && <div style={S.skip}>Not done: the AI gave no change and no reason for this.</div>}
      {r.notFoundInWords && <div style={S.warn}>These aren't your exact words, so check this is what you asked.</div>}
      {r.skipped.map((s, i) => <div key={`s${i}`} style={S.skip}><b>{s.label}.</b> Not done: {s.reason}</div>)}
      {r.notDone.map((n, i) => <div key={`n${i}`} style={S.skip}>Not done{n.path ? ` (${n.path})` : ""}: {n.reason}</div>)}
      {r.hidden.map((h, i) => <div key={`h${i}`} style={S.warn}><b>{h.label}.</b> Written, but it doesn't print: {h.reason}</div>)}
      {r.checks.map((c, i) => <div key={`c${i}`} style={c.ok ? S.printNote : S.warn}>{c.message}</div>)}
      {r.notes.map((n, i) => <div key={`t${i}`} style={S.warn}>{n}</div>)}
    </div>
  );
}

function RawReply({ raw }) {
  if (!raw?.splitter && !raw?.editor) return null;
  return (
    <details style={S.raw}>
      <summary style={S.rawSummary}>Show the AI's reply</summary>
      {raw.splitter && <pre style={S.pre}>{raw.splitter}</pre>}
      {raw.editor && <pre style={S.pre}>{raw.editor}</pre>}
    </details>
  );
}

export function QuoteBoxPreviewBody({ preview, raw }) {
  const p = preview;
  return (
    <div data-testid="box-preview" style={S.preview}>
      <div style={S.previewHead}>Nothing has changed yet. Read this, then Apply or Cancel.</div>
      {p.conflict && <div data-testid="box-conflict" style={S.notAccounted}>{p.conflict}</div>}
      {p.requests.map((r) => <RequestCard key={r.id} r={r} />)}
      {p.notAccounted.map((w, i) => <div key={i} data-testid="box-not-accounted" style={S.notAccounted}>Not accounted for: {w}</div>)}
      {p.unaffiliated.ops.map((op, i) => <div key={i} style={S.skip}><b>{op.label}.</b> {op.status === "applied" ? "This change wasn't tied to anything you asked." : `Not done: ${op.reason}`}</div>)}
      {p.unaffiliated.notDone.map((n, i) => <div key={i} style={S.skip}>Not done{n.path ? ` (${n.path})` : ""}: {n.reason}</div>)}

      {p.rows.length > 0 && (
        <table data-testid="box-rows" style={S.table}>
          <thead><tr><th style={S.th}>Field</th><th style={S.th}>Was</th><th style={S.th}>Now</th></tr></thead>
          <tbody>
            {p.rows.map((r, i) => (
              <tr key={i} data-testid="box-row">
                <td style={S.tdField}>{r.label}{r.reason && <div style={S.warn}>{r.reason}</div>}</td>
                <td style={S.td}>{r.old}</td>
                <td style={S.td}>{r.new}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {p.stored.length > 0 && (
        <div style={S.block}>
          <div style={S.blockHead}>Also stored when saved</div>
          {p.stored.map((r, i) => <div key={i} style={S.note}>{r.label}: {r.old} to {r.new}</div>)}
        </div>
      )}
      {p.page.length > 0 && (
        <div data-testid="box-page" style={S.block}>
          <div style={S.blockHead}>On the printed page</div>
          {p.page.map((f) => (
            <div key={f.section} style={S.fragment}>
              <div style={S.fragmentHead}>{f.label}</div>
              {f.removed.map((t, i) => <div key={`r${i}`} style={S.gone}>No longer: {t}</div>)}
              {f.added.map((t, i) => <div key={`a${i}`} style={S.added}>Now: {t}</div>)}
            </div>
          ))}
        </div>
      )}
      {p.pageUnchanged && <div style={S.printNote}>What prints doesn't change.</div>}

      <div data-testid="box-total" style={S.total}>
        {p.total.changed
          ? <>Total changes: <span style={S.money}>{fmtMoney(p.total.before)}</span> to <span style={S.money}>{fmtMoney(p.total.after)}</span></>
          : <>Total stays <span style={S.money}>{fmtMoney(p.total.after)}</span></>}
      </div>

      {p.removedLines.lines.length > 0 && (
        <div data-testid="box-removed-lines" style={S.confirm}>
          <div style={S.confirmHead}>This removes {p.removedLines.lines.length} line{p.removedLines.lines.length > 1 ? "s" : ""} totaling {fmtMoney(p.removedLines.total)}.</div>
          {p.removedLines.lines.map((li, i) => <div key={i} style={S.note}>{li.description || "Untitled line"}</div>)}
        </div>
      )}
      {(p.discardDays.length > 0 || p.keptDayLoses) && (
        <div data-testid="box-discard-days" style={S.confirm}>
          {p.discardDays.length > 0 && <div style={S.confirmHead}>This discards {p.discardDays.length} day{p.discardDays.length > 1 ? "s" : ""}.</div>}
          {p.discardDays.map((d, i) => <div key={i} style={S.note}>{dayDetails(d)}</div>)}
          {p.keptDayLoses && <div style={S.confirmHead}>The day that stays keeps its date and location. A single-day quote has no place for the rest, so this goes:</div>}
          {p.keptDayLoses && <div style={S.note}>{p.keptDayLoses}</div>}
        </div>
      )}
      {p.frozenMoves.length > 0 && <div style={S.warn}>A LiDAR or drone line moves on the page with this: {p.frozenMoves.join(", ")}. Its quantity and price stay the same.</div>}
      {p.clientMove && (
        <div data-testid="box-client-move" style={S.confirm}>
          <div style={S.confirmHead}>This quote moves to another client.</div>
          <div style={S.note}>From {p.clientMove.fromName || "(no client)"} ({p.clientMove.fromCode || "no code"}) to {p.clientMove.toName || "(no client)"} ({p.clientMove.toCode || "no code"}).</div>
          <div style={S.note}>{p.clientMove.jobId ? `Booking would make job ID ${p.clientMove.jobId}. ` : ""}No CRM record changes.</div>
          {p.clientMove.contactWarning && <div style={S.warn}>{p.clientMove.contactWarning}</div>}
        </div>
      )}
      {p.wordingWarnings?.map((w, i) => <div key={i} style={S.warn}>{w}</div>)}
      {p.contactWarning && <div style={S.warn} data-testid="box-contact-warning">{p.contactWarning}</div>}
      {p.crm.map((t, i) => <div key={i} style={S.printNote}>{t}</div>)}
      {p.jobNotes.map((t, i) => <div key={i} style={S.printNote}>{t}</div>)}
      {p.saveBlocker && <div data-testid="box-save-blocker" style={S.warn}>Saving would refuse this quote as it would be: {p.saveBlocker}</div>}
      {p.saveError && <div style={S.warn}>{p.saveError}</div>}
      {p.sizeRefusal && <div style={S.skip}>{p.sizeRefusal}</div>}
      {p.note && <div style={S.aiNote}><span style={S.was}>The AI's note, not checked</span> {p.note}</div>}
      <RawReply raw={raw} />
    </div>
  );
}

export default function QuoteBoxPanel({ state, onStart, onApply, onCancel, disabled = false }) {
  const [text, setText] = useState("");
  const { phase } = state;
  const busy = phase === "splitting" || phase === "editing";
  const held = phase === "preview";
  const canStart = !!text.trim() && !busy && !held && !disabled;

  return (
    <div data-testid="box-panel" style={S.box}>
      <div style={S.title}>Change this quote with AI</div>
      <textarea
        data-testid="box-input"
        aria-label="What to change on this quote"
        style={S.input}
        rows={3}
        value={text}
        disabled={busy || held || disabled}
        onChange={(e) => setText(e.target.value)}
        placeholder="Say what to change. A list on several lines is fine."
      />
      <div style={S.row}>
        <button type="button" data-testid="box-start" style={{ ...btnP, padding: "8px 14px", fontSize: 12, opacity: canStart ? 1 : 0.4 }} disabled={!canStart} onClick={() => onStart(text)}>
          {phase === "splitting" ? "Reading your request…" : phase === "editing" ? "Working out the changes…" : phase === "error" ? "Try Again" : "Show Changes"}
        </button>
        {busy && <button type="button" style={{ ...btnS, padding: "8px 14px", fontSize: 12 }} onClick={onCancel}>Cancel</button>}
      </div>

      {phase === "error" && (
        <div data-testid="box-error" style={S.errorBox}>
          <div>{state.error}</div>
          <RawReply raw={state.raw} />
        </div>
      )}
      {(phase === "dropped" || phase === "applied") && (
        <div data-testid="box-message" style={S.message}>
          <div>{state.message}</div>
          <RawReply raw={state.raw} />
        </div>
      )}

      {held && (
        <>
          <QuoteBoxPreviewBody preview={state.preview} raw={state.raw} />
          <div style={S.row}>
            {state.preview.canApply
              ? <button type="button" data-testid="box-apply" style={{ ...btnP, padding: "8px 14px", fontSize: 12 }} onClick={() => { if (onApply()?.outcome === "applied") setText(""); }}>{state.preview.applyLabel}</button>
              : <span style={S.noApply}>There is nothing to apply.</span>}
            <button type="button" data-testid="box-cancel" style={{ ...btnS, padding: "8px 14px", fontSize: 12 }} onClick={onCancel}>Cancel</button>
          </div>
        </>
      )}
    </div>
  );
}

const S = {
  box: { background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "16px 18px", fontFamily: font },
  title: { fontSize: 12, fontWeight: 500, color: C.text, textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 10 },
  input: { width: "100%", padding: "10px 13px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, fontSize: 13, fontFamily: font, fontWeight: 300, outline: "none", boxSizing: "border-box", resize: "vertical", lineHeight: 1.5 },
  row: { display: "flex", alignItems: "center", gap: 8, marginTop: 10, flexWrap: "wrap" },
  preview: { marginTop: 12, display: "flex", flexDirection: "column", gap: 10 },
  previewHead: { fontSize: 12.5, color: C.text, fontWeight: 500 },
  request: { border: `1px solid ${C.border}`, borderRadius: 6, padding: "10px 12px", display: "flex", flexDirection: "column", gap: 6 },
  requestHead: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 },
  words: { fontSize: 13, color: C.text, lineHeight: 1.5, whiteSpace: "pre-wrap", minWidth: 0, overflowWrap: "anywhere" },
  badge: { fontSize: 10.5, border: "1px solid", borderRadius: 20, padding: "2px 9px", flexShrink: 0, whiteSpace: "nowrap" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 12, lineHeight: 1.5 },
  th: { textAlign: "left", fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", padding: "4px 6px", borderBottom: `1px solid ${C.border}` },
  tdField: { padding: "6px", verticalAlign: "top", fontWeight: 500, color: C.text, borderBottom: `1px solid ${C.border}`, width: "26%" },
  td: { padding: "6px", verticalAlign: "top", color: C.text, borderBottom: `1px solid ${C.border}`, whiteSpace: "pre-wrap", overflowWrap: "anywhere" },
  block: { display: "flex", flexDirection: "column", gap: 4 },
  blockHead: { fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em" },
  fragment: { background: C.surfaceAlt, borderRadius: 4, padding: "6px 9px", fontSize: 11.5, lineHeight: 1.5 },
  fragmentHead: { fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 2 },
  gone: { color: C.muted, overflowWrap: "anywhere" },
  added: { color: C.text, overflowWrap: "anywhere" },
  was: { fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginRight: 4 },
  note: { fontSize: 12, color: C.muted, lineHeight: 1.5 },
  printNote: { fontSize: 12, color: C.muted, lineHeight: 1.5 },
  warn: { fontSize: 12, color: C.amber, lineHeight: 1.5 },
  skip: { fontSize: 12, color: C.danger, lineHeight: 1.5 },
  notAccounted: { fontSize: 12.5, color: C.danger, lineHeight: 1.5, border: `1px solid ${C.danger}40`, borderRadius: 6, padding: "8px 11px" },
  total: { fontSize: 12.5, color: C.text },
  money: { fontFamily: mono },
  confirm: { border: `1px solid ${C.amber}59`, background: `${C.amber}10`, borderRadius: 6, padding: "10px 12px", display: "flex", flexDirection: "column", gap: 4 },
  confirmHead: { fontSize: 12, fontWeight: 500, color: C.text, lineHeight: 1.5 },
  aiNote: { fontSize: 12, color: C.muted, lineHeight: 1.5 },
  raw: { fontSize: 11.5, color: C.muted },
  rawSummary: { cursor: "pointer" },
  pre: { fontFamily: mono, fontSize: 11, whiteSpace: "pre-wrap", overflowWrap: "anywhere", background: C.surfaceAlt, borderRadius: 4, padding: 8, margin: "6px 0 0" },
  errorBox: { fontSize: 12, color: C.danger, marginTop: 10, lineHeight: 1.5, display: "flex", flexDirection: "column", gap: 6 },
  message: { fontSize: 12, color: C.muted, marginTop: 10, lineHeight: 1.5 },
  noApply: { fontSize: 12, color: C.muted },
};
